
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Loader2 } from 'lucide-react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"

interface DeleteDraftDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    projectId: string
    draftId: string
    draftName: string
    onSuccess?: () => void
}

export function DeleteDraftDialog({
    open,
    onOpenChange,
    projectId,
    draftId,
    draftName,
    onSuccess,
}: DeleteDraftDialogProps) {
    const queryClient = useQueryClient()

    const { mutate: deleteDraft, isPending } = useMutation({
        mutationFn: async () => {
            return api.delete(`/projects/${projectId}/drafts/${draftId}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['drafts', projectId] })
            onOpenChange(false)
            if (onSuccess) onSuccess()
        },
    })

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Delete Draft?</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete <span className="font-semibold text-foreground">{draftName}</span>? This action cannot be undone.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => deleteDraft()}
                        disabled={isPending}
                    >
                        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
